import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { contractors } from '../services/api';

const SPECIALTIES = [
  'plumber', 'electrician', 'landscaper', 'hvac', 'painter',
  'roofer', 'general', 'cleaner', 'elevator', 'locksmith', 'other',
];

const SPECIALTY_COLORS = {
  plumber: 'bg-blue-100 text-blue-700',
  electrician: 'bg-yellow-100 text-yellow-700',
  landscaper: 'bg-green-100 text-green-700',
  hvac: 'bg-cyan-100 text-cyan-700',
  painter: 'bg-pink-100 text-pink-700',
  roofer: 'bg-orange-100 text-orange-700',
  general: 'bg-gray-100 text-gray-700',
  cleaner: 'bg-teal-100 text-teal-700',
  elevator: 'bg-purple-100 text-purple-700',
  locksmith: 'bg-red-100 text-red-700',
  other: 'bg-gray-100 text-gray-600',
};

function StarRating({ rating }) {
  return (
    <span className="flex items-center gap-1">
      <span className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <svg key={s} className={`w-4 h-4 ${s <= Math.round(rating) ? 'text-yellow-400' : 'text-gray-200'}`}
            fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </span>
      <span className="text-xs text-gray-500">{rating > 0 ? rating.toFixed(1) : '—'}</span>
    </span>
  );
}

function ContractorModal({ contractor, onClose, onSave }) {
  const [form, setForm] = useState(contractor || {
    name: '', company: '', email: '', phone: '',
    specialty: '', license_number: '', insurance_expiry: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (contractor?.id) {
        await contractors.update(contractor.id, form);
        toast.success('Contractor updated');
      } else {
        await contractors.create(form);
        toast.success('Contractor added');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-handle sm:hidden" />
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 text-lg">
            {contractor?.id ? 'Edit' : 'Add'} Contractor
          </h2>
          <button onClick={onClose} className="btn-icon text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Name *</label>
              <input className="input" value={form.name} onChange={set('name')} required placeholder="John Smith" />
            </div>
            <div>
              <label className="label">Company</label>
              <input className="input" value={form.company || ''} onChange={set('company')} placeholder="Smith Plumbing Ltd." />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input type="email" inputMode="email" className="input" value={form.email || ''} onChange={set('email')} placeholder="john@example.com" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input type="tel" inputMode="tel" className="input" value={form.phone || ''} onChange={set('phone')} placeholder="604-555-0100" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Specialty</label>
              <select className="input" value={form.specialty || ''} onChange={set('specialty')}>
                <option value="">Select trade…</option>
                {SPECIALTIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">License #</label>
              <input className="input" value={form.license_number || ''} onChange={set('license_number')} placeholder="BC-12345" />
            </div>
          </div>
          <div>
            <label className="label">Insurance Expiry</label>
            <input type="date" className="input" value={form.insurance_expiry || ''} onChange={set('insurance_expiry')} />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={3} value={form.notes || ''} onChange={set('notes')}
              placeholder="Reliable, good pricing on emergency calls…" />
          </div>
          <div className="flex gap-3 pt-1 safe-bottom">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Mobile card ────────────────────────────────────────────────────────────────
function ContractorCard({ c, onEdit, onDelete }) {
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-gray-900">{c.name}</p>
            {c.specialty && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SPECIALTY_COLORS[c.specialty] || SPECIALTY_COLORS.other}`}>
                {c.specialty}
              </span>
            )}
          </div>
          {c.company && <p className="text-sm text-gray-500 mt-0.5">{c.company}</p>}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => onEdit(c)} className="btn-icon text-blue-600 hover:bg-blue-50">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button onClick={() => onDelete(c.id)} className="btn-icon text-red-500 hover:bg-red-50">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        {c.email && (
          <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-sm text-blue-600 truncate">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {c.email}
          </a>
        )}
        {c.phone && (
          <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-sm text-blue-600">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {c.phone}
          </a>
        )}
      </div>

      <div className="mt-2.5"><StarRating rating={c.rating} /></div>

      {c.insurance_expiry && (
        <p className="text-xs text-gray-400 mt-1.5">
          Insurance expires: {new Date(c.insurance_expiry).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
        </p>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function ContractorList() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [modal, setModal] = useState(null);

  const load = () => {
    contractors.list({ search, specialty: filterSpecialty })
      .then((r) => setList(r.data))
      .catch(() => toast.error('Failed to load contractors'));
  };

  useEffect(() => { load(); }, [search, filterSpecialty]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contractor?')) return;
    await contractors.delete(id);
    toast.success('Deleted');
    load();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Contractors</h1>
          <p className="text-gray-500 text-sm">{list.length} in your database</p>
        </div>
        <button onClick={() => setModal('new')} className="btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">Add Contractor</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input className="input pl-9" placeholder="Search name or company…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="input w-auto min-w-36" value={filterSpecialty} onChange={(e) => setFilterSpecialty(e.target.value)}>
          <option value="">All trades</option>
          {SPECIALTIES.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {list.length === 0 ? (
        <div className="card py-16 text-center">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="font-medium text-gray-500">No contractors found</p>
          <button onClick={() => setModal('new')} className="mt-2 text-sm text-blue-600 hover:underline font-medium">
            Add your first contractor →
          </button>
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="sm:hidden space-y-3">
            {list.map((c) => (
              <ContractorCard key={c.id} c={c} onEdit={setModal} onDelete={handleDelete} />
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block card">
            <div className="table-scroll">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {['Name / Company', 'Specialty', 'Contact', 'Rating', 'Actions'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {list.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{c.name}</p>
                        {c.company && <p className="text-xs text-gray-400">{c.company}</p>}
                      </td>
                      <td className="px-4 py-3">
                        {c.specialty ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SPECIALTY_COLORS[c.specialty] || SPECIALTY_COLORS.other}`}>
                            {c.specialty}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {c.email && <p className="text-sm text-gray-700">{c.email}</p>}
                        {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
                      </td>
                      <td className="px-4 py-3"><StarRating rating={c.rating} /></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3">
                          <button onClick={() => setModal(c)} className="text-sm text-blue-600 hover:underline font-medium">Edit</button>
                          <button onClick={() => handleDelete(c.id)} className="text-sm text-red-500 hover:underline font-medium">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {modal && (
        <ContractorModal
          contractor={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
